import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, ValidatorFn } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

import { InputFieldComponent } from '../input-field/input-field.component';
import { PasswordFieldComponent } from '../password-field/password-field.component';
import { SelectFieldComponent } from '../select-field/select-field.component';
import { TextareaFieldComponent } from '../textarea-field/textarea-field.component';
import { CheckboxFieldComponent } from '../checkbox-field/checkbox-field.component';
import { RadioFieldComponent } from '../radio-field/radio-field.component';
import { DatepickerFieldComponent } from '../datepicker-field/datepicker-field.component';
import { CurrencyFieldComponent } from '../currency-field/currency-field.component';
import { MaskFieldComponent } from '../mask-field/mask-field.component';
import { FileFieldComponent } from '../file-field/file-field.component';
import { ColorFieldComponent } from '../color-field/color-field.component';
import { MultiFileFieldComponent } from '../multi-file-field/multi-file-field.component';
import { MultiSelectFieldComponent } from '../multi-select-field/multi-select-field.component';
import { ButtonComponent } from '../../layout/buttons/button/button.component';

import {
  DynamicFormConfig,
  DynamicFieldConfig,
  DynamicFormOutput,
  FieldValidation,
} from '../../core/models/dynamic-form.models';

/**
 * IsDynamicFormComponent
 *
 * Gera formulários completos a partir de uma configuração JSON.
 * Retorna os valores como objeto ao submeter.
 *
 * @example
 * <is-dynamic-form
 *   [config]="formConfig"
 *   (submitted)="onSubmit($event)"
 *   (cancelled)="onCancel()"
 * ></is-dynamic-form>
 */
@Component({
  selector: 'is-dynamic-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    InputFieldComponent,
    PasswordFieldComponent,
    SelectFieldComponent,
    TextareaFieldComponent,
    CheckboxFieldComponent,
    RadioFieldComponent,
    DatepickerFieldComponent,
    CurrencyFieldComponent,
    MaskFieldComponent,
    FileFieldComponent,
    ColorFieldComponent,
    MultiFileFieldComponent,
    MultiSelectFieldComponent,
    ButtonComponent,
  ],
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, OnChanges {
  @Input() config!: DynamicFormConfig;
  @Input() loading = false;

  @Output() submitted = new EventEmitter<DynamicFormOutput>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() valueChanged = new EventEmitter<{ [key: string]: any }>();

  form!: FormGroup;

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].firstChange) {
      this.buildForm();
    }
  }

  /** Submete o formulário e emite os valores */
  onSubmit(): void {
    this.form.markAllAsTouched();

    const rawValues = this.form.getRawValue();
    const values = this.serializeValues(rawValues);
    const files = this.extractFiles(rawValues);

    const output: DynamicFormOutput = {
      values,
      files,
      valid: this.form.valid,
    };

    this.submitted.emit(output);
  }

  /** Emite evento de cancelamento */
  onCancel(): void {
    this.cancelled.emit();
  }

  /** Reseta o formulário para os valores iniciais */
  reset(): void {
    this.buildForm();
  }

  /** Retorna os valores atuais do formulário */
  getValues(): { [key: string]: any } {
    return this.form.getRawValue();
  }

  /** Verifica se um campo é visível */
  isVisible(field: DynamicFieldConfig): boolean {
    return field.visible !== false;
  }

  /** Retorna a classe de coluna Bootstrap */
  getColClass(field: DynamicFieldConfig): string {
    const col = field.colSize || this.config.columns || 12;
    return `col-md-${col}`;
  }

  // ─── Privados ──────────────────────────────────────────────────────────────

  private buildForm(): void {
    const group: { [key: string]: any } = {};

    for (const field of this.config.fields) {
      const validators = this.buildValidators(field.validation);
      let value: any;

      if (field.type === 'checkbox' || field.type === 'switch') {
        value = field.value ?? false;
      } else if (field.type === 'file' || field.type === 'multi-file') {
        value = null;
      } else {
        value = field.value ?? '';
      }

      group[field.key] = [
        { value, disabled: field.disabled ?? false },
        validators,
      ];
    }

    this.form = this.fb.group(group);

    // Emite mudanças de valor
    this.form.valueChanges.subscribe((values) => {
      this.valueChanged.emit(values);
    });
  }

  private buildValidators(validation?: FieldValidation): ValidatorFn[] {
    if (!validation) return [];

    const validators: ValidatorFn[] = [];

    if (validation.required) validators.push(Validators.required);
    if (validation.email) validators.push(Validators.email);
    if (validation.minLength) validators.push(Validators.minLength(validation.minLength));
    if (validation.maxLength) validators.push(Validators.maxLength(validation.maxLength));
    if (validation.min !== undefined) validators.push(Validators.min(validation.min));
    if (validation.max !== undefined) validators.push(Validators.max(validation.max));
    if (validation.pattern) validators.push(Validators.pattern(validation.pattern));

    return validators;
  }

  /** Converte valores do formulário para formato serializável (JSON-safe) */
  private serializeValues(rawValues: { [key: string]: any }): { [key: string]: any } {
    const result: { [key: string]: any } = {};

    for (const field of this.config.fields) {
      const value = rawValues[field.key];

      if (field.type === 'file') {
        result[field.key] = this.isFile(value)
          ? { name: value.name, size: value.size, type: value.type }
          : null;
      } else if (field.type === 'multi-file') {
        result[field.key] = Array.isArray(value)
          ? value.filter((f: any) => this.isFile(f)).map((f: File) => ({ name: f.name, size: f.size, type: f.type }))
          : [];
      } else {
        result[field.key] = value;
      }
    }

    return result;
  }

  /** Extrai os File objects originais para o componente pai poder fazer upload */
  private extractFiles(rawValues: { [key: string]: any }): { [key: string]: File | File[] } {
    const files: { [key: string]: File | File[] } = {};

    for (const field of this.config.fields) {
      const value = rawValues[field.key];

      if (field.type === 'file' && this.isFile(value)) {
        files[field.key] = value;
      } else if (field.type === 'multi-file' && Array.isArray(value) && value.length > 0) {
        files[field.key] = value.filter((f: any) => this.isFile(f));
      }
    }

    return files;
  }

  /** Verifica se um valor é um File object */
  private isFile(value: any): value is File {
    return value != null && typeof value === 'object' && 'name' in value && 'size' in value && 'lastModified' in value;
  }

  constructor(private fb: FormBuilder) {}
}
